<template>
  <div class="sj-range">
    <div class="main">
      <!-- 选择器主体，由bar和circle及对应的placeholder构成 -->
      <!-- clrcle位置根据value计算 -->
      <div class="handle">
        <div
          class="handle-bar"
          :style="{
            backgroundColor: placeholderColor
          }"
        >
          <div
            class="center-bar"
            :style="{
              left: `${displayValueA}%`,
              width: `${displayValueB - displayValueA}%`,
              backgroundColor: color
            }"
          ></div>
          <div
            class="circle"
            :style="{ left: '0', borderColor: placeholderColor }"
          ></div>
          <div
            class="circle"
            :style="{ left: '100%', borderColor: placeholderColor }"
          ></div>
          <div
            class="handle-circle"
            :style="{ left: `${displayValueA}%`, borderColor: color }"
            @mousedown.prevent="dragStart($event, 'A')"
          ></div>
          <div
            class="handle-circle"
            :style="{ left: `${displayValueB}%`, borderColor: color }"
            @mousedown.prevent="dragStart($event, 'B')"
          ></div>
        </div>
      </div>
    </div>
    <!-- 在拖动时显示对应label -->
    <div class="labels" v-if="labels.length">
      <div
        class="label"
        :class="{ show: target === 'A' }"
        :style="{ left: `${displayValueA}%`, color: textColor }"
      >
        {{ labels[valueA] }}
      </div>
      <div
        class="label"
        :class="{ show: target === 'B' }"
        :style="{ left: `${displayValueB}%`, color: textColor }"
      >
        {{ labels[valueB] }}
      </div>
    </div>
  </div>
</template>

<script>
/**
 * range组件，用于在一个范围内选定一个范围
 */
import isEqual from "lodash.isequal";

export default {
  name: "SjRange",
  data() {
    return {
      /**
       * 组件维护两个值，valueA和valueB，显示为两个圆圈
       */
      valueA: 0,
      valueB: 0,
      /**
       * 当前选中的是A还是B，确认拖拽对象
       */
      target: null
    };
  },
  props: {
    /**
     * 可选范围为索引值数组
     */
    range: {
      type: Array,
      // required: true,
      validator(v) {
        return (
          v instanceof Array &&
          typeof v[0] === "number" &&
          typeof v[1] === "number"
        );
      }
    },
    /**
     * value为索引值数组
     */
    value: {
      type: Array,
      default() {
        return null;
      },
      validator(v) {
        return (
          v instanceof Array &&
          typeof v[0] === "number" &&
          typeof v[1] === "number"
        );
      }
    },
    /**
     * label也为数组，是value对应索引值的文本
     */
    labels: {
      type: Array,
      default() {
        return [];
      }
    },
    /**
     * 选中柱形颜色
     */
    color: String,
    /**
     * 底柱形颜色
     */
    placeholderColor: String,
    /**
     * 文本颜色
     */
    textColor: String
  },
  computed: {
    /**
     * 计算valueA和valueB相对dom容器的偏移百分比
     */
    displayValueA() {
      if (this.range[1] - this.range[0] === 0) return 0;
      return (
        ((this.valueA - this.range[0]) / (this.range[1] - this.range[0])) * 100
      );
    },
    displayValueB() {
      if (this.range[1] - this.range[0] === 0) return 100;
      return (
        ((this.valueB - this.range[0]) / (this.range[1] - this.range[0])) * 100
      );
    }
  },
  methods: {
    /**
     * 开始拖拽，添加事件
     */
    dragStart(e, t) {
      this.target = t;
      window.addEventListener("mousemove", this.dragMove);
      window.addEventListener("mouseup", this.dragEnd);
      window.addEventListener("contextmenu", this.dragEnd);
      e.stopPropagation();
    },
    /**
     * 拖拽时根据鼠标事件位置计算value，拖拽时不上报input
     */
    dragMove(e) {
      let x = e.clientX;
      const area = this.getArea();
      if (x < area.left) {
        x = area.left;
      }
      if (x > area.right) {
        x = area.right;
      }
      const percentage = (x - area.left) / (area.right - area.left);
      const v = Math.round(
        percentage * (this.range[1] - this.range[0]) + this.range[0]
      );
      if (this[`value${this.target}`] !== v) {
        this[`value${this.target}`] = v;
        /**
         * 在拖拽一端超过另一端时交换当前选中的target
         */
        if (this.valueB < this.valueA) {
          this.target = this.target === "A" ? "B" : "A";
        }
      }
    },
    /**
     * 结束拖拽，上报input，删除事件
     */
    dragEnd() {
      this.$emit("input", [
        Math.min(this.valueA, this.valueB),
        Math.max(this.valueA, this.valueB)
      ]);
      this.$emit("dragEnd", [
        Math.min(this.valueA, this.valueB),
        Math.max(this.valueA, this.valueB)
      ]);
      this.target = null;
      window.removeEventListener("mousemove", this.dragMove);
      window.removeEventListener("mouseup", this.dragEnd);
      window.removeEventListener("contextmenu", this.dragEnd);
    },
    /**
     * 辅助函数，获取range的dom容器坐标
     */
    getArea() {
      const box = this.$el.querySelector(".handle-bar").getBoundingClientRect();
      return {
        left: box.left,
        right: box.right
      };
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        /**
         * 初值处理方法
         */
        if (value === null) {
          this.valueA = this.range[0];
          this.valueB = this.range[1];
          this.$emit("input", [this.range[0], this.range[1]]);
        } else {
          this.valueA = value[0];
          this.valueB = value[1];
          if (this.valueA < this.range[0]) {
            this.valueA = this.range[0];
          }
          if (this.valueA > this.range[1]) {
            this.valueA = this.range[1];
          }

          if (this.valueB < this.range[0]) {
            this.valueB = this.range[0];
          }
          if (this.valueB > this.range[1]) {
            this.valueB = this.range[1];
          }
          if (this.valueA > this.valueB) {
            const temp = this.valueA;
            this.valueA = this.valueB;
            this.valueB = temp;
          }
        }
      }
    },
    range: {
      // immediate: true,
      handler(range, old) {
        if (!this.value) {
          this.valueA = range[0];
          this.valueB = range[1];
        } else {
          if (!isEqual(range, old)) {
            this.valueA = range[0];
            this.valueB = range[1];
            // if (this.valueA < range[0]) this.valueA = range[0];
            // if (this.valueB > range[1]) this.valueB = range[1];
          }
        }
        this.$emit("input", [this.valueA, this.valueB]);
      }
    }
  }
};
</script>

<style scoped lang="less">
@primary-color: @color-available;
@placeholder-color: fade(@primary-color, 30%);
@text-color: @color-dark;

.sj-range {
  position: relative;
  .main {
    display: flex;
    align-items: center;
    padding: 5px;
    height: 40px;
    box-sizing: border-box;

    .panel {
      flex-shrink: 0;
    }

    .handle {
      flex-grow: 1;
      position: relative;
      padding: 0 20px;
    }

    .handle-bar {
      position: relative;
      width: 100%;
      height: 2px;
      background-color: @placeholder-color;
    }

    .circle {
      position: absolute;
      top: -5px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      box-sizing: border-box;
      transform: translateX(-50%);
      border: 2px solid @placeholder-color;
      background-color: #fff;
    }

    .handle-circle {
      .circle;
      border: 2px solid @primary-color;
      cursor: pointer;
    }

    .center-bar {
      position: relative;
      height: 2px;
      background-color: @primary-color;
    }
  }
  .labels {
    left: 0;
    right: 0;
    top: -5px;
    position: absolute;
    margin: 0 26px;
    pointer-events: none;
    /*height: 20px;*/
    .label {
      .text("caption", @text-color, "regular");
      position: absolute;
      transform: translateX(-50%);
      transition: opacity 0.1s ease;
      opacity: 0;
      word-break: keep-all;
      white-space: nowrap;
    }
    .show {
      opacity: 1;
    }
  }
}
</style>
